// -- Fabric UI

import React, { useEffect, useState } from "react";

import { Badge, Button, makeStyles, Persona } from "@fluentui/react-components";
import { AlertRegular } from "@fluentui/react-icons";
import { ROUTES } from "library/routes";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";

import { AppName } from "components/AppName/AppName";
import { ChatPanel } from "components/ChatPanel/ChatPanel";
import { HeaderDocumentLinks } from "components/HeaderDocumentLinks/HeaderDocumentLinks";
import { HelpLauncher, type HelpNotice } from "help/HelpLauncher";
import { helpLanguageFor } from "help/types";
import { useIsMobile } from "hooks/useIsMobile";
import { useInvalidateCompanyBenefitPackageViews } from "state/useGetCompanyBenefitPackageViews";
import { useInvalidateEmployeeData } from "state/useGetEmployeeData";
import { useInvalidateEmployeeEnrollments } from "state/useGetEnrollments";

import { GrecoPanel } from "./components/GrecoPanel/GrecoPanel";
import { IGrecoHeaderStrings } from "./components/GrecoPanel/IGrecoHeaderStrings";
import { IGrecoPanelStrings } from "./components/GrecoPanel/IGrecoPanelStrings";
import { PanelType } from "./components/GrecoPanel/PanelType";
import { SettingsMenu } from "./components/SettingsMenu/SettingsMenu";

import AppLogo from "../../assets/hbhLogo.svg";
import { useAppTheme } from "../../hooks/useAppTheme";
import { devices, sizes } from "../../library/constants";
import { INotification, SelectItem } from "../../library/types";
import { Flex } from "../styled";

interface IProps {
  headerConfig?: any;
  user?: any;
  panelStatus?: (status: boolean) => void;
  logo?: any;

  headerStrings?: IGrecoHeaderStrings;
  notifications?: INotification[];
  navigation?: JSX.Element;
  clearNotifications?: () => void;
  languageOptions?: SelectItem[];
  defaultLanguage?: string;
  onChangeLanguage: (
    event: React.FormEvent<HTMLDivElement>,
    option?: SelectItem,
    index?: number
  ) => void;
  isMobile?: boolean;
}

export const iconStyle = makeStyles({
  icon: {
    height: "50px",
    width: "50px",
    color: "white",
    fontSize: "26px",
  },
});

type statePanelType = PanelType | null;

const GrecoHeader = (props: IProps) => {
  const navigate = useNavigate();
  const theme = useAppTheme();
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [panelType, setPanelType] = useState<statePanelType>(null);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [panelStrings, setPanelStrings] = useState<IGrecoPanelStrings | undefined>();
  const [headerConfig, setHeaderConfig] = useState({
    application_name: "GrECo Header",
    notifications_visible: true,
    help_visible: true,
    account: true,
    search_autofocus: false,
    logo_visible: true,
    title_visible: true,
    settings_visible: true,
    defaultLanguage: "en-GB",
    navigationPanelOpen: false,
  });

  const invalidateEmployee = useInvalidateEmployeeData();
  const invalidateEmployeeEnrollments = useInvalidateEmployeeEnrollments();
  const invalidateCompanyBenefitPackageViews = useInvalidateCompanyBenefitPackageViews();

  useEffect(() => {
    if (!props.headerConfig) return;
    setHeaderConfig((prev) => ({ ...prev, ...props.headerConfig }));
  }, [props.headerConfig]);

  useEffect(() => {
    if (!props.headerStrings || !panelType) return;

    if (panelType !== null) {
      const panelStr = updatePanelStrings(panelType, props.headerStrings) as IGrecoPanelStrings;
      setPanelStrings(panelStr);
    }
  }, [panelType, props.headerStrings]);

  const updatePanelStrings = (
    panelType: PanelType,
    headerStrings
  ): IGrecoPanelStrings | undefined => {
    switch (panelType) {
      case PanelType.Notifications:
        return headerStrings.notificationsPanelStrings;
      case PanelType.UserSettings:
        return headerStrings.settingsPanelStrings;

      default:
        return undefined;
    }
  };

  const togglePanel = (type) => (type === panelType ? closePanel() : openPanel(type));

  const openPanel = (type) => {
    if (props.panelStatus) props.panelStatus(true);
    setPanelIsOpen(true);
    setPanelType(type);
  };

  const closePanel = () => {
    if (props.panelStatus) props.panelStatus(false);
    setPanelIsOpen(false);
    setPanelType(null);
  };

  return (
    <Container>
      <GrecoPanel
        onDismiss={closePanel}
        panelIsOpen={panelIsOpen}
        panelType={panelType}
        userData={props.user}
        headerConfig={headerConfig}
        panelStrings={panelStrings}
        notifications={props.notifications}
        clearNotifications={props.clearNotifications}
        isMobile={props.isMobile}
        togglePanel={togglePanel as () => void}
        languageOptions={props.languageOptions}
        defaultSelectedLanguage={props.defaultLanguage || headerConfig.defaultLanguage}
        onChangeLanguage={props.onChangeLanguage}
      />

      <ChatPanel />

      <ContentWrap>
        <Flex wrap="nowrap" $gap={"8px"}>
          {headerConfig.logo_visible ? (
            <img
              onClick={() => {
                invalidateEmployee();
                invalidateEmployeeEnrollments();
                invalidateCompanyBenefitPackageViews();
              }}
              className="logo"
              src={props.logo}
              alt={headerConfig.application_name}
              style={{
                height: "38px",
                cursor: "pointer",
              }}
            />
          ) : null}
          <img
            onClick={() => {
              navigate(ROUTES.HOME);
            }}
            src={AppLogo}
            style={{
              cursor: "pointer",
              paddingLeft: "8px",
              borderLeft: `2px solid ${theme.palette.blueDark}`,
              width: "41px",
              height: "38px",
            }}
            alt="app_logo"
          />
          {headerConfig.logo_visible ? <AppName /> : null}
        </Flex>
        <ActionIconsWrap>
          <HeaderDocumentLinks />
          {isDesktop && (
            <SettingsMenu
              languageOptions={props.languageOptions || []}
              onSelect={props.onChangeLanguage}
              selectedLanguage={props.defaultLanguage || ""}
            />
          )}
          <HelpLauncher
            pathname={pathname}
            language={helpLanguageFor(i18n.language)}
            viewer={HELP_VIEWER}
            notify={notifyHelp}
            iconColor={theme.palette.purpleDark}
          />
          <NotificationsButtonWrap>
            <Button
              data-help="app-notifications"
              icon={<AlertRegular fontSize={25} />}
              onClick={() => togglePanel(PanelType.Notifications)}
              appearance="transparent"
              style={{
                color: theme.palette.purpleDark,
                transition: "all 0.2s ease-in-out",
                paddingBottom: "0px",
              }}
            />
            <Badge
              style={{
                position: "absolute",
                top: "-4px",
                right: "-8px",
                pointerEvents: "none",
                // Badge centers its content with inline-flex - "block" would
                // knock the number off-center.
                display: props.notifications?.length ? "inline-flex" : "none",
              }}
            >
              {props.notifications?.length}
            </Badge>
          </NotificationsButtonWrap>

          {isMobile && (
            <Persona
              data-help="app-settings"
              onClick={() => togglePanel(PanelType.UserSettings)}
              avatar={{
                image: {
                  src: props?.user?.backgroundPicturePath || "",
                },
                name: props?.user?.emailAddress,
              }}
              secondaryText=""
              size="large"
            />
          )}
        </ActionIconsWrap>
      </ContentWrap>
    </Container>
  );
};

export default GrecoHeader;

const Container = styled.div`
  background: ${({ theme }) => theme.palette.white};
  display: flex;
  width: 100%;

  line-height: ${sizes.appHeader}px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  width: 100%;
  // box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin: 0 auto;
  position: sticky;
  top: 0;
`;

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: ${sizes.appHeader}px;
  padding: 0 10px;
  @media only screen and ${devices.md} {
    width: ${sizes.appContentWidthDesktop}px;
    padding: 0 10px;
    margin: 0 auto;
    box-sizing: border-box;
  }
`;

// Anchors the notifications badge to the bell button itself. Keeping the badge
// outside the Button also stops the Button from clipping it.
const NotificationsButtonWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ActionIconsWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  @media only screen and ${devices.md} {
  }
`;

/** The page guide's viewer flags — a single tier today (see `HelpViewer`). */
const HELP_VIEWER = {};

/** The page guide's notices go through the app's own toasts. */
const notifyHelp = ({ severity, message, action }: HelpNotice): void => {
  const show =
    severity === "success" ? toast.success : severity === "warning" ? toast.warning : toast.info;
  show(
    action ? (
      <span>
        {message}{" "}
        <Button size="small" appearance="transparent" onClick={action.onClick}>
          {action.label}
        </Button>
      </span>
    ) : (
      message
    ),
    { autoClose: action ? 7000 : 5000 }
  );
};
